<template>
  <div class="rm-screen">
    <div v-if="loading" class="rm-empty">
      <v-progress-circular indeterminate color="#c2410c"></v-progress-circular>
    </div>

    <template v-else>
      <div class="rm-summary">
        <div class="rm-summary__row">
          <div class="rm-summary__labels">
            <span>{{ $t("taken") }}</span>
            <span>
              {{ pluralT("report_mobile.companies_n", packingCompanies.length) }} ·
              {{ pluralT("report_mobile.meals_n", summary.ordered) }}
            </span>
          </div>
          <span class="rm-summary__value">
            {{ summary.taken }}<span class="rm-summary__value-of"> / {{ summary.ordered }}</span>
          </span>
        </div>
        <div
          class="rm-progress rm-progress--inverse"
          role="progressbar"
          :aria-valuenow="summary.taken"
          aria-valuemin="0"
          :aria-valuemax="summary.ordered"
        >
          <div class="rm-progress__fill" :style="{ width: `${summary.percent}%` }"></div>
        </div>
      </div>

      <article
        v-for="company in packingCompanies"
        :key="company.key"
        class="rm-card rm-pack"
        :class="{ 'is-open': company.key === openKey }"
      >
        <button
          type="button"
          class="rm-pack__head"
          :aria-expanded="String(company.key === openKey)"
          @click="toggleOpen(company.key)"
        >
          <span class="rm-pack__title">
            <span class="rm-pack__name">{{ company.name }}</span>
            <span v-if="company.subtitle" class="rm-muted">{{ company.subtitle }}</span>
          </span>
          <span class="rm-pack__progress-text rm-num" :class="{ 'is-complete': company.complete }">
            {{ company.taken }} / {{ company.ordered }}
          </span>
          <v-icon
            size="18"
            color="#8a817a"
            class="rm-pack__chevron"
            :class="{ 'is-open': company.key === openKey }"
          >
            mdi-chevron-down
          </v-icon>
        </button>

        <div class="rm-pack__bar">
          <div
            class="rm-progress"
            :class="{ 'rm-progress--complete': company.complete }"
            role="progressbar"
            :aria-valuenow="company.taken"
            aria-valuemin="0"
            :aria-valuemax="company.ordered"
          >
            <div class="rm-progress__fill" :style="{ width: `${company.percent}%` }"></div>
          </div>
        </div>

        <ul v-if="company.key === openKey" class="rm-list rm-pack__items">
          <li v-for="item in company.items" :key="item.key" class="rm-pack__item">
            <span class="rm-pack__item-text">
              <span class="rm-pack__item-name">{{ item.name }}</span>
              <span v-if="item.combination" class="rm-muted">{{ item.combination }}</span>
              <span class="rm-muted rm-num">
                {{ $t("report_mobile.taken_n", { n: item.taken }) }}
              </span>
            </span>
            <span class="rm-pack__item-count rm-num">{{ item.ordered }}</span>
          </li>
        </ul>
      </article>

      <div v-if="!packingCompanies.length" class="rm-empty">
        {{
          filters.search
            ? $t("report_mobile.no_results_for", { search: filters.search })
            : $t("report_mobile.no_meals")
        }}
      </div>
    </template>
  </div>
</template>

<script>
import ReportAllCompaniesTabPacking from "@/components/report/all-companies/tabs/ReportAllCompaniesTabPacking.vue";
import hasPlural from "../../shared/hasPlural";

/**
 * Mobilni prikaz "Sve firme – zbirni izvještaj → Pakovanje": nasleđuje učitavanje,
 * štampu i Excel iz ReportAllCompaniesTabPacking ("naručeno / preuzeto" po firmi).
 */
export default {
  name: "ReportMobileAllPacking",
  extends: ReportAllCompaniesTabPacking,
  mixins: [hasPlural],
  props: {
    /** Smene stižu kroz selectedShifts (prop roditelja); ovde samo da se ne prikažu kao atribut. */
    shifts: {
      type: Array,
      default: () => [],
    },
  },
  data: () => ({
    /** undefined = prva firma otvorena, null = sve zatvoreno */
    expandedKey: undefined,
  }),
  computed: {
    packingCompanies() {
      const search = this.filters.search;
      return this.visibleCompanies
        .map((company) => {
          const companyMatches = !search || this.matches(company, ["name"], search);
          const items = company.items
            .filter((item) => companyMatches || this.matches(item, ["name", "combination"], search))
            .map((item) => ({
              key: `${item.id}-${item.combination || ""}`,
              name: this.toLatin(item.name),
              combination: item.combination ? this.toLatin(item.combination) : "",
              ordered: item.total,
              taken: item.total_payed,
            }));
          const ordered = items.reduce((sum, item) => sum + item.ordered, 0);
          const taken = items.reduce((sum, item) => sum + item.taken, 0);
          return {
            key: company.id || company.name,
            name: company.name,
            subtitle: [company.address, company.city].filter(Boolean).join(", "),
            items,
            ordered,
            taken,
            complete: ordered > 0 && taken >= ordered,
            percent: this.percentOf(taken, ordered),
          };
        })
        .filter((company) => company.items.length);
    },
    summary() {
      const ordered = this.packingCompanies.reduce((sum, company) => sum + company.ordered, 0);
      const taken = this.packingCompanies.reduce((sum, company) => sum + company.taken, 0);
      return { ordered, taken, percent: this.percentOf(taken, ordered) };
    },
    openKey() {
      if (this.expandedKey !== undefined) {
        return this.expandedKey;
      }
      return this.packingCompanies[0]?.key ?? null;
    },
  },
  methods: {
    matches(row, fields, search) {
      return this.customSearch([row], fields.map((value) => ({ value })), search).length > 0;
    },
    percentOf(part, whole) {
      return whole > 0 ? Math.min(100, Math.round((part / whole) * 100)) : 0;
    },
    toggleOpen(key) {
      this.expandedKey = this.openKey === key ? null : key;
    },
  },
};
</script>
